import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import { workordersApi, ORDER_TYPES, PRIORITIES, STATUSES, type WorkOrder, type WorkOrderCreate } from '../api/workorders'
import { usersApi } from '../api/users'
import { useAuthStore } from '../store/authStore'
import { LeafletMap, FALLBACK_CENTER } from '../components/Map'

// Priority-colored circle markers
function makeIcon(priority: string): L.DivIcon {
  const color =
    priority === 'urgent' ? '#E62E00' :
    priority === 'high'   ? '#FF8A1A' : '#8892B0'
  return L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -10],
  })
}

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function fmtDateTime(iso: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('sr-Latn-RS', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('sr-Latn-RS', { hour: '2-digit', minute: '2-digit' })
}


function priorityInfo(p: string) {
  return PRIORITIES.find((x) => x.value === p) ?? PRIORITIES[2]
}

function statusInfo(s: string) {
  return STATUSES.find((x) => x.value === s) ?? STATUSES[0]
}

const NEXT_STATUS: Record<string, string> = {
  pending:     'assigned',
  assigned:    'in_progress',
  in_progress: 'completed',
}
const NEXT_LABEL: Record<string, string> = {
  pending:     'Dodeliti',
  assigned:    'Pokrenuti',
  in_progress: 'Završiti',
}

const CAN_CREATE_ROLES = ['admin', 'company_admin', 'dispatcher']
const PAGE_SIZE = 15

// ── Badge components ──────────────────────────────────────────────────────────

function PriorityBadge({ priority }: { priority: string }) {
  const { label, color } = priorityInfo(priority)
  return <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-semibold ${color}`}>{label}</span>
}

function StatusBadge({ status }: { status: string }) {
  const { label, color } = statusInfo(status)
  return <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-semibold ${color}`}>{label}</span>
}

// ── Create modal ──────────────────────────────────────────────────────────────

function CreateModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState<WorkOrderCreate>({
    title: '', address: '', order_type: 'repair', priority: 'medium',
  })
  const [err, setErr] = useState('')

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list().then((r) => r.data),
    staleTime: 60_000,
  })
  const assignable = users.filter((u) => u.active && !u.is_placeholder)

  const mutation = useMutation({
    mutationFn: () => workordersApi.create(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['workorders'] }); onClose() },
    onError: () => setErr('Greška pri kreiranju naloga.'),
  })

  const set = (key: keyof WorkOrderCreate) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-base font-bold text-gray-900 mb-5">Novi radni nalog</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Naslov <span className="text-red-500">*</span></label>
            <input type="text" value={form.title} onChange={set('title')}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-hidden" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tip</label>
              <select value={form.order_type} onChange={set('order_type')}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-hidden bg-white">
                {ORDER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Prioritet</label>
              <select value={form.priority} onChange={set('priority')}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-hidden bg-white">
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Adresa <span className="text-red-500">*</span></label>
            <input type="text" value={form.address} onChange={set('address')}
              placeholder="npr. Ulica kralja Petra 15, Mionica"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-hidden" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Dodeli korisniku</label>
              <select value={form.assigned_to ?? ''} onChange={set('assigned_to')}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-hidden bg-white">
                <option value="">— Nedodeljeno —</option>
                {assignable.map((u) => (
                  <option key={u.user_id} value={u.user_id}>{u.full_name ?? u.email ?? u.user_id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Planirani datum</label>
              <input type="date" value={form.scheduled_date ?? ''} onChange={set('scheduled_date')}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-hidden" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Opis / napomena</label>
            <textarea rows={3} value={form.description ?? ''} onChange={set('description')}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-hidden resize-none" />
          </div>
        </div>

        {err && <p className="mt-3 text-xs text-red-600">{err}</p>}

        <div className="flex gap-2 mt-6">
          <button
            onClick={() => { setErr(''); mutation.mutate() }}
            disabled={mutation.isPending || !form.title || !form.address}
            className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium py-2.5 rounded-lg disabled:opacity-50"
          >
            {mutation.isPending ? 'Kreiranje…' : 'Kreirati nalog'}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
            Otkazati
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Assign modal ──────────────────────────────────────────────────────────────

function AssignModal({ wo, onClose }: { wo: WorkOrder; onClose: () => void }) {
  const qc = useQueryClient()
  const [assignedTo, setAssignedTo] = useState(wo.assigned_to ?? '')
  const [scheduledDate, setScheduledDate] = useState(wo.scheduled_date?.slice(0, 10) ?? '')
  const [err, setErr] = useState('')

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list().then((r) => r.data),
    staleTime: 60_000,
  })
  const assignable = users.filter((u) => u.active && !u.is_placeholder)

  const mutation = useMutation({
    mutationFn: () =>
      workordersApi.update(wo.id, {
        assigned_to: assignedTo || null,
        scheduled_date: scheduledDate || null,
        status: 'assigned',
        version: wo.version,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workorders'] })
      onClose()
    },
    onError: () => setErr('Greška pri dodeli naloga.'),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <h2 className="text-base font-bold text-gray-900">Dodeli nalog</h2>
        <p className="text-sm text-gray-600 -mt-2 truncate">{wo.title}</p>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Dodeli korisniku <span className="text-red-500">*</span>
          </label>
          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-cyan outline-hidden bg-white"
          >
            <option value="">— Izaberi korisnika —</option>
            {assignable.map((u) => (
              <option key={u.user_id} value={u.user_id}>
                {u.full_name ?? u.email}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Datum (opciono)</label>
          <input
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-cyan outline-hidden"
          />
        </div>

        {err && <p className="text-xs text-red-600">{err}</p>}

        <div className="flex gap-3 pt-1">
          <button
            onClick={() => mutation.mutate()}
            disabled={!assignedTo || mutation.isPending}
            className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark disabled:opacity-50 text-brand-midnight text-sm font-medium py-2 rounded-lg"
          >
            {mutation.isPending ? '…' : 'Dodeliti'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2 rounded-lg"
          >
            Otkazati
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Work order card ───────────────────────────────────────────────────────────

function WorkOrderCard({ wo, canManage }: { wo: WorkOrder; canManage: boolean }) {
  const qc = useQueryClient()
  const [expanded, setExpanded] = useState(false)
  const [assigning, setAssigning] = useState(false)

  const statusMutation = useMutation({
    mutationFn: (newStatus: string) =>
      workordersApi.updateStatus(wo.id, newStatus, wo.version),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workorders'] }),
  })

  const cancelMutation = useMutation({
    mutationFn: () => workordersApi.updateStatus(wo.id, 'cancelled', wo.version),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workorders'] }),
  })

  const priorityMutation = useMutation({
    mutationFn: (p: string) => workordersApi.updatePriority(wo.id, p, wo.version),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workorders'] }),
  })

  const next = NEXT_STATUS[wo.status]
  const isDone = wo.status === 'completed' || wo.status === 'cancelled'

  return (
    <div className={`bg-white rounded-xl border shadow-xs overflow-hidden transition-shadow hover:shadow-md ${
      wo.priority === 'urgent' ? 'border-brand-critical' : 'border-gray-100'
    }`}>
      {/* Priority stripe */}
      {wo.priority === 'urgent' && <div className="h-1 bg-brand-critical" />}
      {wo.priority === 'high'   && <div className="h-1 bg-brand-warning" />}

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <PriorityBadge priority={wo.priority} />
              <StatusBadge status={wo.status} />
              <span className="text-xs text-gray-400">
                {ORDER_TYPES.find((t) => t.value === wo.order_type)?.label ?? wo.order_type}
              </span>
            </div>
            <h3 className="text-sm font-semibold text-gray-900 leading-tight">{wo.title}</h3>
            <p className="text-xs text-gray-500 mt-0.5 truncate">{wo.address}</p>
          </div>

          <button
            onClick={() => setExpanded((e) => !e)}
            className="shrink-0 text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* Meta row */}
        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
          {wo.assigned_to_name && (
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {wo.assigned_to_name}
            </span>
          )}
          {wo.scheduled_date && (
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {fmtDate(wo.scheduled_date)}
            </span>
          )}
          <span className="text-gray-400">{fmtDateTime(wo.created_at)}</span>
        </div>

        {/* Expanded details */}
        {expanded && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
            {wo.description && <p className="text-xs text-gray-600">{wo.description}</p>}
            {wo.notes && wo.notes !== wo.description && (
              <p className="text-xs text-gray-500 italic">{wo.notes}</p>
            )}

            {/* Photo */}
            {wo.photo_path && wo.photo_path.startsWith('/api/v1/') && (
              <a href={wo.photo_path} target="_blank" rel="noreferrer">
                <img
                  src={wo.photo_path}
                  alt="Fotografija kvara"
                  className="w-full rounded-lg object-cover max-h-48 mt-1 border border-gray-100"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
              </a>
            )}

            {/* Timeline */}
            <div className="space-y-1 pt-1">
              <p className="text-xs text-gray-400">Kreiran: {fmtDateTime(wo.created_at)}</p>
              {wo.updated_at !== wo.created_at && (
                <p className="text-xs text-gray-400">Ažurirano: {fmtDateTime(wo.updated_at)}</p>
              )}
              {wo.scheduled_date && (
                <p className="text-xs text-gray-400">Zakazano: {fmtDate(wo.scheduled_date)}</p>
              )}
              {wo.completed_date && (
                <p className="text-xs text-emerald-600">Završeno: {fmtDateTime(wo.completed_date)}</p>
              )}
            </div>

            {/* Priority change */}
            {canManage && !wo.status.match(/completed|cancelled/) && (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs text-gray-500">Prioritet:</span>
                <select
                  value={wo.priority}
                  disabled={priorityMutation.isPending}
                  onChange={(e) => priorityMutation.mutate(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white focus:ring-1 focus:ring-blue-500 outline-hidden disabled:opacity-50"
                >
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {canManage && !isDone && (
          <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
            {next && (
              <button
                onClick={() => wo.status === 'pending' ? setAssigning(true) : statusMutation.mutate(next)}
                disabled={statusMutation.isPending}
                className="flex-1 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-xs font-semibold py-2 rounded-lg disabled:opacity-50"
              >
                {statusMutation.isPending ? '…' : NEXT_LABEL[wo.status]}
              </button>
            )}
            <button
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              className="px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
            >
              Otkazati
            </button>
          </div>
        )}
      </div>

      {assigning && <AssignModal wo={wo} onClose={() => setAssigning(false)} />}
    </div>
  )
}

// ── Status column (with per-column "load more") ───────────────────────────────

function StatusColumn({ title, orders, canManage, emptyMsg }: {
  title: string
  orders: WorkOrder[]
  canManage: boolean
  emptyMsg: string
}) {
  const [visible, setVisible] = useState(PAGE_SIZE)

  // Reset when orders change (filter switch)
  const shown   = orders.slice(0, visible)
  const hasMore = orders.length > visible
  const remaining = orders.length - visible

  return (
    <div className="flex flex-col min-w-[280px] max-w-xs">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <span className="text-xs bg-gray-200 text-gray-600 font-semibold px-2 py-0.5 rounded-full">
          {orders.length}
        </span>
      </div>
      <div className="flex-1 space-y-3">
        {orders.length === 0 ? (
          <p className="text-xs text-gray-400 italic px-1">{emptyMsg}</p>
        ) : (
          <>
            {shown.map((wo) => (
              <WorkOrderCard key={wo.id} wo={wo} canManage={canManage} />
            ))}
            {hasMore && (
              <button
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="w-full py-2 text-xs font-medium text-brand-cyan-text hover:text-brand-midnight hover:bg-brand-cyan-tint rounded-lg border border-brand-cyan-tint transition-colors"
              >
                Prikaži još {Math.min(PAGE_SIZE, remaining)} od {remaining} preostalih
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ── Map view ──────────────────────────────────────────────────────────────────

function MapView({ orders }: { orders: WorkOrder[] }) {
  const mapped = orders.filter(
    (w) => typeof w.location_lat === 'number' && typeof w.location_lng === 'number',
  )
  const center: [number, number] = mapped.length
    ? [
        mapped.reduce((s, w) => s + w.location_lat, 0) / mapped.length,
        mapped.reduce((s, w) => s + w.location_lng, 0) / mapped.length,
      ]
    : FALLBACK_CENTER

  if (mapped.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        Nijedan nalog nema koordinate
      </div>
    )
  }

  const priorityLabel: Record<string, string> = {
    urgent: 'Hitno', high: 'Visok', medium: 'Srednji', low: 'Nizak',
  }
  const statusLabel: Record<string, string> = {
    pending: 'Na čekanju', assigned: 'Dodeljen', in_progress: 'U toku',
    completed: 'Završen', cancelled: 'Otkazan',
  }

  return (
    <LeafletMap center={center} zoom={14}>
      {mapped.map((wo) => (
        <Marker
          key={wo.id}
          position={[wo.location_lat, wo.location_lng]}
          icon={makeIcon(wo.priority)}
        >
          <Popup maxWidth={260}>
            <div className="text-sm space-y-1 py-1">
              <p className="font-semibold leading-tight">{wo.title}</p>
              <p className="text-gray-500 text-xs">{wo.address}</p>
              <div className="flex gap-2 text-xs pt-1">
                <span className="font-medium text-gray-700">
                  {priorityLabel[wo.priority] ?? wo.priority}
                </span>
                <span className="text-gray-400">·</span>
                <span className="text-gray-600">{statusLabel[wo.status] ?? wo.status}</span>
              </div>
              {wo.assigned_to_name && (
                <p className="text-xs text-gray-500">Dodeljeno: {wo.assigned_to_name}</p>
              )}
            </div>
          </Popup>
        </Marker>
      ))}
    </LeafletMap>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function DispatcherView() {
  const { user } = useAuthStore()
  const canManage = user?.roles.some((r) => CAN_CREATE_ROLES.includes(r)) ?? false
  const [creating, setCreating] = useState(false)
  const [filterStatus, setFilterStatus] = useState<string>('active')
  const [viewMode, setViewMode] = useState<'kanban' | 'map'>('kanban')

  const { data: allOrders = [], isLoading } = useQuery({
    queryKey: ['workorders'],
    queryFn: () => workordersApi.list({ limit: 200 }).then((r) => r.data),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  // Separate into kanban columns
  const active   = allOrders.filter((w) => !['completed', 'cancelled'].includes(w.status))
  const done     = allOrders.filter((w) => ['completed', 'cancelled'].includes(w.status))
  const displayed = filterStatus === 'active' ? active : done

  const pending     = displayed.filter((w) => w.status === 'pending')
  const assigned    = displayed.filter((w) => w.status === 'assigned')
  const in_progress = displayed.filter((w) => w.status === 'in_progress')
  const completed   = displayed.filter((w) => w.status === 'completed')
  const cancelled   = displayed.filter((w) => w.status === 'cancelled')

  const urgentCount = active.filter((w) => w.priority === 'urgent').length

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3 flex-wrap">
        {urgentCount > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg">
            <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" />
            <span className="text-xs font-semibold text-orange-700">{urgentCount} hitnih naloga</span>
          </div>
        )}

        {/* Status filter */}
        <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
          {[
            { key: 'active', label: `Aktivni (${active.length})` },
            { key: 'done',   label: `Završeni (${done.length})` },
          ].map(({ key, label }) => (
            <button key={key} onClick={() => setFilterStatus(key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filterStatus === key ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* View mode toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
          <button
            onClick={() => setViewMode('kanban')}
            title="Kanban tabla"
            className={`px-2.5 py-1.5 rounded-md transition-colors ${
              viewMode === 'kanban' ? 'bg-white shadow-xs text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('map')}
            title="Mapa"
            className={`px-2.5 py-1.5 rounded-md transition-colors ${
              viewMode === 'map' ? 'bg-white shadow-xs text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </button>
        </div>

        <div className="flex-1" />

        {canManage && (
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-2 bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight text-sm font-medium px-4 py-2 rounded-lg"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Novi nalog
          </button>
        )}
      </div>

      {/* Board / Map */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-7 h-7 border-4 border-brand-cyan border-t-transparent rounded-full animate-spin" />
          </div>
        ) : viewMode === 'map' ? (
          <div className="h-full">
            <MapView orders={displayed} />
          </div>
        ) : filterStatus === 'active' ? (
          <div className="flex gap-5 h-full p-6 overflow-auto">
            <StatusColumn title="Na čekanju"  orders={pending}     canManage={canManage} emptyMsg="Nema naloga" />
            <StatusColumn title="Dodeljeni"   orders={assigned}    canManage={canManage} emptyMsg="Nema dodeljenih" />
            <StatusColumn title="U toku"      orders={in_progress} canManage={canManage} emptyMsg="Nema aktivnih" />
          </div>
        ) : (
          <div className="flex gap-5 h-full p-6 overflow-auto">
            <StatusColumn title="Završeni"  orders={completed}  canManage={false} emptyMsg="Nema završenih" />
            <StatusColumn title="Otkazani"  orders={cancelled}  canManage={false} emptyMsg="Nema otkazanih" />
          </div>
        )}
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} />}
    </div>
  )
}
